import { useState } from "react";
import { 
  FileText, 
  Download, 
  Printer,
  BookOpen,
  Users,
  Presentation,
  BarChart3,
  Shield,
  Settings,
  Activity,
  Library,
  Server,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Zap,
  Target,
  Clock,
  Award,
  MessageSquare,
  Mic,
  Video,
  Eye,
  Link2,
  Database,
  FileSpreadsheet,
  Cpu,
  Globe,
  Key,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

export default function Documentation() {
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    admin: false,
    user: false,
    features: false,
    api: false,
    technical: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const content = document.getElementById('documentation-content');
    const blob = new Blob([content.innerText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'EVOLVE_Documentation.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" id="documentation-content">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <BookOpen className="w-7 h-7 text-primary" />
            EVOLVE Documentation
          </h1>
          <p className="text-[#5C5C5C]">Complete guide to the AI-powered presentation training platform</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint} className="rounded-full">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button onClick={handleDownload} className="rounded-full bg-primary">
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>
      </div>

      {/* Version Info */}
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-lg text-[#1A1A1A]">EVOLVE Platform</h2>
                <p className="text-sm text-[#5C5C5C]">AI-Powered Communication & Presentation Training</p>
              </div>
            </div>
            <div className="text-right">
              <Badge className="mb-1">Version 2.0</Badge>
              <p className="text-xs text-[#5C5C5C]">Last Updated: December 2025</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table of Contents */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Table of Contents</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <a href="#overview" className="flex items-center gap-2 text-primary hover:underline">
                <CheckCircle className="w-4 h-4" /> 1. Platform Overview
              </a>
              <a href="#admin" className="flex items-center gap-2 text-primary hover:underline">
                <Shield className="w-4 h-4" /> 2. Admin Panel Features
              </a>
              <a href="#user" className="flex items-center gap-2 text-primary hover:underline">
                <Users className="w-4 h-4" /> 3. User Portal Features
              </a>
              <a href="#features" className="flex items-center gap-2 text-primary hover:underline">
                <Presentation className="w-4 h-4" /> 4. Core Features
              </a>
            </div>
            <div className="space-y-2">
              <a href="#api" className="flex items-center gap-2 text-primary hover:underline">
                <Database className="w-4 h-4" /> 5. API Integration
              </a>
              <a href="#technical" className="flex items-center gap-2 text-primary hover:underline">
                <Server className="w-4 h-4" /> 6. Technical Details
              </a>
              <a href="#scoring" className="flex items-center gap-2 text-primary hover:underline">
                <Target className="w-4 h-4" /> 7. Scoring System
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 1: Overview */}
      <Card id="overview">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 rounded-t-lg"
          onClick={() => toggleSection('overview')}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              1. Platform Overview
            </span>
            {expandedSections.overview ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.overview && (
          <CardContent className="space-y-4">
            <div className="prose max-w-none">
              <h3 className="text-lg font-semibold text-[#1A1A1A]">What is EVOLVE?</h3>
              <p className="text-[#5C5C5C]">
                EVOLVE is an AI-powered internal platform designed for employees to practice and improve their 
                communication and presentation skills. The platform provides a safe environment to present, 
                receive AI-generated feedback, and track improvement over time.
              </p>
              
              <h3 className="text-lg font-semibold text-[#1A1A1A] mt-4">Key Benefits</h3>
              <ul className="space-y-2 text-[#5C5C5C]">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong>Practice Anytime:</strong> 24/7 access to practice presentations without audience anxiety</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong>AI Feedback:</strong> Instant, objective feedback on presentation skills using GPT-5.1</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong>Voice Q&A:</strong> Practice answering questions verbally with AI-powered transcription</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong>Progress Tracking:</strong> Monitor improvement over time with detailed analytics</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                  <span><strong>Presentation Library:</strong> Learn from colleagues&apos; presentations</span>
                </li>
              </ul>
              
              <h3 className="text-lg font-semibold text-[#1A1A1A] mt-4">User Roles</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Shield className="w-4 h-4 text-primary" /> Admin (L&D/Leadership)
                  </h4>
                  <p className="text-sm text-[#5C5C5C] mt-2">
                    Full access to user management, analytics, compliance tracking, and system configuration.
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" /> User (Employee)
                  </h4>
                  <p className="text-sm text-[#5C5C5C] mt-2">
                    Access to practice sessions, feedback, library, and personal progress tracking.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Section 2: Admin Panel */}
      <Card id="admin">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 rounded-t-lg"
          onClick={() => toggleSection('admin')}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              2. Admin Panel Features
            </span>
            {expandedSections.admin ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.admin && (
          <CardContent className="space-y-6">
            <div className="grid gap-4">
              {/* Dashboard */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <BarChart3 className="w-4 h-4 text-primary" /> Dashboard
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  Overview of platform metrics with date filters (Today, This Month, Last 3 Months, etc.)
                </p>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• Total users, active sessions, completion rates</li>
                  <li>• Department-wise performance charts</li>
                  <li>• Recent activity timeline</li>
                  <li>• Top performers leaderboard</li>
                </ul>
              </div>
              
              {/* User Management */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Users className="w-4 h-4 text-primary" /> User Management
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  Complete user lifecycle management with advanced features
                </p>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• Add users with Name, Email, Department, Designation, TEK ID, Location</li>
                  <li>• Soft delete and restore functionality</li>
                  <li>• Multi-select bulk actions (activate, deactivate, delete)</li>
                  <li>• <strong>Export CSV</strong> - Full user data export</li>
                  <li>• <strong>Export ATS</strong> - Simple format (Name, Email, Profile URL)</li>
                  <li>• Copy shareable profile links</li>
                  <li>• Edit presentation time limits per user</li>
                  <li>• Track last login time</li>
                  <li>• Profile picture captured from first session</li>
                </ul>
              </div>
              
              {/* Import API Configuration */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <RefreshCw className="w-4 h-4 text-primary" /> Import API (External User Sync)
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  Sync users from external HR/ERP systems
                </p>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• Configure external API endpoint URL</li>
                  <li>• Authentication options: Bearer Token, API Key, Basic Auth</li>
                  <li>• Field mapping (map external fields to EVOLVE fields)</li>
                  <li>• Test connection before syncing</li>
                  <li>• Manual or scheduled sync (Daily/Weekly)</li>
                  <li>• Auto-create or update existing users</li>
                </ul>
              </div>
              
              {/* EVOLVE API */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Key className="w-4 h-4 text-primary" /> EVOLVE API (Outbound)
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  Allow external systems to access EVOLVE user data
                </p>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• Generate secure API keys with custom names</li>
                  <li>• View and revoke active API keys</li>
                  <li>• Complete API documentation with examples</li>
                  <li>• Endpoints: GET /users, GET /users/profile-urls, POST /users</li>
                  <li>• Rate limiting: 100 requests/minute</li>
                </ul>
              </div>
              
              {/* Library Management */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Library className="w-4 h-4 text-primary" /> Library Management
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  Control presentation visibility and access
                </p>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• Toggle presentation visibility (public/hidden)</li>
                  <li>• Multi-select bulk activate/deactivate</li>
                  <li>• View counts for each presentation</li>
                  <li>• Filter by visibility status, department</li>
                </ul>
              </div>
              
              {/* Reports & Analytics */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <BarChart3 className="w-4 h-4 text-primary" /> Reports & Analytics
                </h4>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• <strong>Sessions Tab:</strong> Detailed session reports with PPT preview/download</li>
                  <li>• <strong>LLM Credit Usage Tab:</strong> Track AI usage and costs</li>
                  <li>• Department-level analytics</li>
                  <li>• Compliance tracking dashboard</li>
                  <li>• Activity logs with filters</li>
                  <li>• Export reports to Excel/PDF</li>
                </ul>
              </div>
              
              {/* LLM Credit Usage */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Cpu className="w-4 h-4 text-primary" /> LLM Credit Usage Tracking
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  Monitor AI usage and operational costs
                </p>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• Total tokens consumed per month</li>
                  <li>• Cost estimates by operation (Analysis, Q&A, Transcription, Topics)</li>
                  <li>• Usage by AI model (GPT-5.1, Whisper, Sora 2)</li>
                  <li>• Daily usage trend charts</li>
                  <li>• Month-over-month comparison</li>
                  <li>• Recent LLM activity log</li>
                </ul>
              </div>
              
              {/* System Status */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Server className="w-4 h-4 text-primary" /> System Status
                </h4>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• Monitor PyMuPDF, python-pptx status</li>
                  <li>• Database connection health</li>
                  <li>• Storage directory status</li>
                  <li>• Auto-repair functionality</li>
                </ul>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Section 3: User Portal */}
      <Card id="user">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 rounded-t-lg"
          onClick={() => toggleSection('user')}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              3. User Portal Features
            </span>
            {expandedSections.user ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.user && (
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Presentation className="w-4 h-4 text-primary" /> Session Flow
                </h4>
                <ol className="text-sm text-[#5C5C5C] mt-2 space-y-2">
                  <li>1. <strong>Create Session:</strong> Enter topic name and overview</li>
                  <li>2. <strong>Upload Presentation:</strong> PPT, PPTX, PDF, ODF supported</li>
                  <li>3. <strong>Present:</strong> Record video while presenting (25 min default)</li>
                  <li>4. <strong>Voice Q&A:</strong> Answer 3 AI-generated questions verbally</li>
                  <li>5. <strong>AI Analysis:</strong> Receive comprehensive feedback</li>
                  <li>6. <strong>Review:</strong> View scores, replay, and improvement plan</li>
                </ol>
              </div>
              
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Library className="w-4 h-4 text-primary" /> Presentation Library
                </h4>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• <strong>All Library:</strong> Browse all public presentations</li>
                  <li>• <strong>My Library:</strong> View your own presentations</li>
                  <li>• Search by topic, presenter, or department</li>
                  <li>• Grid/List view toggle</li>
                  <li>• Sort by newest, oldest, or most viewed</li>
                </ul>
              </div>
              
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Activity className="w-4 h-4 text-primary" /> Profile Features
                </h4>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• Personalized greeting (Good Morning/Afternoon/Evening)</li>
                  <li>• Session history with scores</li>
                  <li>• Activity log with date filters</li>
                  <li>• AI Topic Generator for ideas</li>
                  <li>• Shareable profile link</li>
                </ul>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Section 4: Core Features */}
      <Card id="features">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 rounded-t-lg"
          onClick={() => toggleSection('features')}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Presentation className="w-5 h-5 text-primary" />
              4. Core Features
            </span>
            {expandedSections.features ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.features && (
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Video className="w-4 h-4 text-primary" /> Video Recording
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  Record presentations with webcam. Timer countdown (25 min default). 
                  Auto-submission when time expires. Profile picture captured from first session.
                </p>
              </div>
              
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Mic className="w-4 h-4 text-primary" /> Voice Q&A
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  3 AI-generated questions based on topic. Speak answers (60 sec max each). 
                  Transcribed by Whisper AI. Analyzed for confidence and clarity.
                </p>
              </div>
              
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <MessageSquare className="w-4 h-4 text-primary" /> AI Feedback
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  GPT-5.1 powered analysis. Scores for content, structure, verbal communication, 
                  confidence, engagement. Slide-by-slide feedback. Improvement plan.
                </p>
              </div>
              
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Eye className="w-4 h-4 text-primary" /> IP & Device Tracking
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  Tracks IP address, location (city, country), browser, OS, device type, 
                  and timezone for security and compliance monitoring.
                </p>
              </div>
              
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Video className="w-4 h-4 text-primary" /> AI Landing Video
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  AI-generated intro video using Sora 2 showcasing platform features.
                  Generated on-demand from the landing page &quot;Know more about EVOLVE&quot; button.
                </p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Section 5: API Integration */}
      <Card id="api">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 rounded-t-lg"
          onClick={() => toggleSection('api')}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" />
              5. API Integration
            </span>
            {expandedSections.api ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.api && (
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              {/* EVOLVE API */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <Key className="w-4 h-4 text-primary" /> EVOLVE API (Outbound)
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  External systems can access EVOLVE data using API keys
                </p>
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-medium">Available Endpoints:</p>
                  <div className="grid gap-2 text-sm">
                    <div className="p-2 bg-slate-50 rounded flex items-center gap-2">
                      <Badge variant="outline" className="text-green-600 border-green-300">GET</Badge>
                      <code>/api/external/users</code>
                      <span className="text-[#5C5C5C]">- All users with profile URLs</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded flex items-center gap-2">
                      <Badge variant="outline" className="text-green-600 border-green-300">GET</Badge>
                      <code>/api/external/users/profile-urls</code>
                      <span className="text-[#5C5C5C]">- ATS format (name, email, URL)</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded flex items-center gap-2">
                      <Badge variant="outline" className="text-green-600 border-green-300">GET</Badge>
                      <code>/api/external/users/:id</code>
                      <span className="text-[#5C5C5C]">- Single user by ID or email</span>
                    </div>
                    <div className="p-2 bg-slate-50 rounded flex items-center gap-2">
                      <Badge variant="outline" className="text-blue-600 border-blue-300">POST</Badge>
                      <code>/api/external/users</code>
                      <span className="text-[#5C5C5C]">- Create new user</span>
                    </div>
                  </div>
                  <p className="text-xs text-[#5C5C5C] mt-2">
                    Authentication: Include <code>X-API-Key</code> header with your API key
                  </p>
                </div>
              </div>
              
              {/* Import API */}
              <div className="p-4 border rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 text-[#1A1A1A]">
                  <RefreshCw className="w-4 h-4 text-primary" /> Import API (Inbound)
                </h4>
                <p className="text-sm text-[#5C5C5C] mt-2">
                  Pull user data from external HR/ERP systems into EVOLVE
                </p>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• Supports any REST API returning JSON user data</li>
                  <li>• Flexible field mapping</li>
                  <li>• Bearer Token, API Key, or Basic Auth</li>
                  <li>• Connection testing before sync</li>
                  <li>• Manual or scheduled sync options</li>
                </ul>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Section 6: Technical Details */}
      <Card id="technical">
        <CardHeader 
          className="cursor-pointer hover:bg-slate-50 rounded-t-lg"
          onClick={() => toggleSection('technical')}
        >
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Server className="w-5 h-5 text-primary" />
              6. Technical Details
            </span>
            {expandedSections.technical ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </CardTitle>
        </CardHeader>
        {expandedSections.technical && (
          <CardContent className="space-y-4">
            <div className="grid gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-[#1A1A1A]">Technology Stack</h4>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• <strong>Frontend:</strong> React 18, TailwindCSS, Shadcn UI, Recharts</li>
                  <li>• <strong>Backend:</strong> FastAPI (Python), Motor (async MongoDB)</li>
                  <li>• <strong>Database:</strong> MongoDB</li>
                  <li>• <strong>AI:</strong> OpenAI GPT-5.1, Whisper (transcription), Sora 2 (video)</li>
                  <li>• <strong>File Processing:</strong> PyMuPDF, python-pptx</li>
                  <li>• <strong>Integrations:</strong> Emergent LLM Key for unified AI access</li>
                </ul>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-[#1A1A1A]">Supported File Formats</h4>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• <strong>Presentations:</strong> PPT, PPTX, PDF, ODP, ODF, KEY</li>
                  <li>• <strong>Video:</strong> WebM (recorded), MP4</li>
                  <li>• <strong>Audio:</strong> WebM, WAV (for transcription)</li>
                </ul>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-[#1A1A1A]">Data Collections</h4>
                <ul className="text-sm text-[#5C5C5C] mt-2 space-y-1">
                  <li>• <strong>users:</strong> User profiles with designation, TEK ID, location, profile picture</li>
                  <li>• <strong>sessions:</strong> Presentation sessions with feedback</li>
                  <li>• <strong>feedback:</strong> AI-generated analysis and scores</li>
                  <li>• <strong>activity_logs:</strong> User activity tracking</li>
                  <li>• <strong>access_logs:</strong> IP and device tracking</li>
                  <li>• <strong>llm_usage:</strong> AI token usage and cost tracking</li>
                  <li>• <strong>evolve_api_keys:</strong> External API credentials</li>
                  <li>• <strong>api_config:</strong> Import API configuration</li>
                </ul>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Section 7: Scoring System */}
      <Card id="scoring">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            7. Scoring System
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-[#5C5C5C]">
            EVOLVE uses a strict scoring system designed to encourage continuous improvement. 
            The AI evaluates presentations like a Fortune 500 executive would.
          </p>
          
          <div className="grid gap-3">
            <div className="flex items-center gap-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold">
                90+
              </div>
              <div>
                <h4 className="font-semibold text-emerald-800">EXCEPTIONAL</h4>
                <p className="text-sm text-emerald-700">TED Talk quality, virtually flawless, world-class presentation</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                80-89
              </div>
              <div>
                <h4 className="font-semibold text-blue-800">EXCELLENT</h4>
                <p className="text-sm text-blue-700">Professional level, very strong with minor improvements possible</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-3 bg-teal-50 rounded-lg border border-teal-200">
              <div className="w-16 h-16 rounded-full bg-teal-500 flex items-center justify-center text-white font-bold">
                70-79
              </div>
              <div>
                <h4 className="font-semibold text-teal-800">GOOD</h4>
                <p className="text-sm text-teal-700">Solid performance with clear areas for improvement</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold">
                60-69
              </div>
              <div>
                <h4 className="font-semibold text-amber-800">AVERAGE</h4>
                <p className="text-sm text-amber-700">Meets basic expectations but needs significant work</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4 p-3 bg-red-50 rounded-lg border border-red-200">
              <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center text-white font-bold">
                &lt;60
              </div>
              <div>
                <h4 className="font-semibold text-red-800">NEEDS IMPROVEMENT</h4>
                <p className="text-sm text-red-700">Notable weaknesses that must be addressed</p>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-slate-50 rounded-lg mt-4">
            <h4 className="font-semibold text-[#1A1A1A]">Evaluation Criteria</h4>
            <div className="grid md:grid-cols-2 gap-2 mt-2 text-sm text-[#5C5C5C]">
              <div>• Content Clarity</div>
              <div>• Structure & Flow</div>
              <div>• Verbal Communication</div>
              <div>• Confidence & Presence</div>
              <div>• Engagement Level</div>
              <div>• Slide Effectiveness</div>
              <div>• Q&A Handling</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center text-sm text-[#9CA3AF] py-4 border-t">
        <p>© {new Date().getFullYear()} EVOLVE Platform - AI-Powered Presentation Training</p>
        <p className="mt-1">Password: <code className="bg-slate-100 px-2 py-0.5 rounded">EvolveTW</code></p>
      </div>
    </div>
  );
}
